import { encodeAbiParameters, parseAbiParameters } from 'viem'
import { SiloConvertToAssetsTypedAbi } from '../../../abis/silo-v2/Silo'
import { Call } from '../../../utils/multicall'
import {
  getPositionLensFor,
  LENS_FAMILY,
  PositionLensAbi,
} from '../position-lens'
import { resolveSiloRoster, type SiloRosterEntry } from './roster'

const ASSET_TYPE_PROTECTED = 0
const ASSET_TYPE_COLLATERAL = 1

export const SILO_V2_USER_CALLS_PER_SIDE = 5
export const SILO_V2_USER_CALLS_PER_PAIR = SILO_V2_USER_CALLS_PER_SIDE * 2

/** Words per lens row — must match `PositionLens.silo`'s documented layout. */
export const SILO_LENS_WORDS = 10

/**
 * True when this chain's Silo reads go through `PositionLens`. Derived from the
 * address map so the builder and the converter cannot disagree about the layout.
 */
export const usesSiloLens = (chainId: string): boolean =>
  getPositionLensFor(chainId, LENS_FAMILY.SILO) !== undefined

/**
 * ONE call for the whole roster instead of 10 per pair.
 *
 * The lens probes the six share balances across both sides and stops there for a
 * pair the account is not in — which is nearly all of them — so `convertToAssets`
 * (twice per side) and `maxRepay` are paid only where there is something to
 * report. Measured on Ethereum: 1,220 calls for 122 pairs, against one.
 *
 * The share-token addresses and decimals come from published metadata rather than
 * `SiloConfig.getSilos()` / `getShareTokens()`, because resolving them on-chain
 * would cost two calls per pair PAID ON EMPTY PAIRS — the exact cost this exists
 * to remove.
 */
const buildSiloLensCall = (
  chainId: string,
  roster: SiloRosterEntry[],
  account: string,
): Call[] => {
  const lens = getPositionLensFor(chainId, LENS_FAMILY.SILO)
  if (!lens || roster.length === 0) return []
  const side = (h: any) => [
    h.silo,
    h.protectedShareToken,
    h.debtShareToken,
    Number(h.decimals),
  ]
  const payload = encodeAbiParameters(
    parseAbiParameters(
      '((address,address,address,uint8),(address,address,address,uint8))[]',
    ),
    [roster.map((r) => [side(r.market.silo0), side(r.market.silo1)]) as any],
  )
  return [
    {
      address: lens,
      name: 'getUserData',
      params: [[{ family: LENS_FAMILY.SILO, payload }], account],
      abi: PositionLensAbi as any,
    },
  ]
}

/** The per-pair read set: 5 slots per side, in the order the converter expects. */
export const buildSiloPairCalls = (
  market: { silo0: any; silo1: any },
  account: string,
): Call[] => {
  const calls: Call[] = []
  for (const half of [market.silo0, market.silo1]) {
    const oneUnit = BigInt(10) ** BigInt(half.decimals)
    calls.push(
      { address: half.silo, name: 'balanceOf', params: [account] },
      {
        address: half.protectedShareToken,
        name: 'balanceOf',
        params: [account],
      },
      {
        address: half.silo,
        name: 'convertToAssets',
        params: [oneUnit, ASSET_TYPE_COLLATERAL],
        abi: SiloConvertToAssetsTypedAbi,
      },
      {
        address: half.silo,
        name: 'convertToAssets',
        params: [oneUnit, ASSET_TYPE_PROTECTED],
        abi: SiloConvertToAssetsTypedAbi,
      },
      { address: half.silo, name: 'maxRepay', params: [account] },
    )
  }
  return calls
}

/**
 * @param params when present, the per-pair lender keys this query stands in for —
 *        `organizeUserQueries` collapses Silo's per-pair keys into one query so the
 *        lens is called once rather than once per pair.
 */
export const buildSiloV2UserCall = (
  chainId: string,
  lender: string,
  account: string,
  params?: any,
): Call[] => {
  const roster = resolveSiloRoster(chainId, lender, params)
  if (roster.length === 0) return []

  // A chain without a Silo-capable deployment keeps the per-pair walk, in roster
  // order. That is the rollout switch and the emergency revert both.
  if (usesSiloLens(chainId)) {
    return buildSiloLensCall(chainId, roster, account)
  }
  return roster.flatMap((r) => buildSiloPairCalls(r.market, account))
}
